import "./Dashboard.css";

import { FullRightArrowDark } from "../../../assets";
import { DashboardMapConstants } from "../../../constants";
import { PROJECT_PHASE_MAP_COLOUR_MAP } from "../../../constants/project-phase";
import { TabsList } from "../../../models";
import { DeveloperSingleCardWithTabsTemplate } from "../../../templates";
import { UpcomingMilestonesTaskPanel } from "../../../templates/UpcomingMilestonesTaskPanel";
import { WorkflowTaskPanel } from "../../../templates/WorkflowTaskPanel";
import { YourTasksPanel } from "../../../templates/YourTasksPanel";
import {
  Button,
  Column,
  CursorDataGrid,
  DashboardMap,
  InventoryBreakdownKPI,
  KanaTooltip,
  Row,
  UpcomingMilestones,
} from "../../../widget";
import { DashboardPieCard, ProjectInvitationBanner } from "../../shared/components";
import { useDashboard } from "./useDashboard";

const Dashboard = (): JSX.Element => {
  const {
    phaseChartData,
    isLoading,
    currentUserType,
    unitMeasurementTotals,
    isLoadingUnitMeasurementTotals,
    selectedStatuses,
    setSelectedStatuses,
    isLoadingTasks,
    columnsTasks,
    tasksDataLoad,
    upcomingMilestones,
    isLoadingMilestones,
    hasMilestonesError,
    isUpcomingMilestonesPanelOpen,
    setIsUpcomingMilestonesPanelOpen,
    isYourTasksPanelOpen,
    setIsYourTasksPanelOpen,
    isTasks,
    projectPins,
    isLoadingProjectPins,
    isMapExpanded,
    setIsMapExpanded,
    workflow,
    selectedPanelTask,
    showWorkflowTaskPanel,
    setSelectedPanelTask,
    setShowWorkflowTaskPanel,
    handleCloseWorkflowTaskPanel,
    handleRefresh,
    refreshTasks,
  } = useDashboard();

  const tabsList: TabsList = [
    { label: "Projects", route: "projects" },
    { label: "Activities", route: "activities" },
  ];

  const carousel = (
    <div className="Dashboard">
      <ProjectInvitationBanner />

      {isLoading && (
        <Row spacingV="ml">
          <Column span={6}>
            <DashboardPieCard
              title="Project phase"
              data={{}}
              tooltip={
                <KanaTooltip
                  tooltipHeader="Project Phase:"
                  tooltipText="Shows the number of projects by phases of their workflow."
                />
              }
              isGradient
              colours="roadmap"
            />
          </Column>
          <Column span={6}>
            <div className="DLoadingCard">
              <p className="body01">Inventory breakdown</p>
              <p className="body2 NoData">Loading...</p>
            </div>
          </Column>
        </Row>
      )}

      {!isLoading && (
        <Row spacingV="ml">
          <Column span={6}>
            <DashboardPieCard
              title="Project phase"
              data={phaseChartData}
              tooltip={
                <KanaTooltip
                  tooltipHeader="Project phase:"
                  tooltipText="Shows the number of projects by phases of their workflow."
                />
              }
              isGradient
              colours="roadmap"
            />
          </Column>
          <Column span={6}>
            <InventoryBreakdownKPI
              measurements={unitMeasurementTotals}
              isLoading={isLoadingUnitMeasurementTotals}
              selectedStatuses={selectedStatuses}
              onStatusesChange={setSelectedStatuses}
            />
          </Column>
        </Row>
      )}

      <Row spacingV="ml" className={`CardsRow${isMapExpanded ? " CardsRow_mapExpanded" : ""}`}>
        <Column span={isMapExpanded ? 12 : 8} className="CardsColumn">
          <div className="MilestoneCard">
            <UpcomingMilestones
              milestones={upcomingMilestones}
              onViewAll={() => setIsUpcomingMilestonesPanelOpen(true)}
              isLoading={isLoadingMilestones}
              hasError={hasMilestonesError}
              loadingCardCount={4}
            />
          </div>
          <div className={`TaskTableContainer${!isTasks ? " TaskTableContainer--empty" : ""}`}>
            <div className="TaskTableHeader">
              <div className="TaskTableHeaderAndIcon">
                <p className="body01">Your tasks</p>
                <KanaTooltip
                  tooltipHeader="Your tasks:"
                  tooltipText="View and manage the tasks that are assigned to you."
                />
              </div>
              <div className="TaskViewAllButton">
                <Button
                  text="View more"
                  onClick={() => setIsYourTasksPanelOpen(true)}
                  variant="secondary"
                  icon={<FullRightArrowDark />}
                  minWidth={false}
                  hasNoPadding
                />
              </div>
            </div>
            <div className="TaskTableWrapper">
              <CursorDataGrid
                data={[]}
                columns={columnsTasks}
                clientSide={false}
                onChange={tasksDataLoad}
                dataIsLoading={isLoadingTasks}
                noDataMessage="No tasks to display"
                showTotalCount={false}
                hasBottomMargin={false}
                sortable={false}
                refresh={refreshTasks}
                hasNoPadding
                hasScrollButton={false}
              />
            </div>
          </div>
        </Column>
        <Column span={isMapExpanded ? 12 : 4} className="MapColumn">
          <DashboardMap
            pins={projectPins}
            product="hub"
            height={DashboardMapConstants.HEIGHT}
            expandedHeight={DashboardMapConstants.EXPANDED_HEIGHT}
            colourBy="phase"
            phaseColours={PROJECT_PHASE_MAP_COLOUR_MAP}
            isLoading={isLoadingProjectPins}
            isExpandable
            isExpanded={isMapExpanded}
            onExpandToggle={setIsMapExpanded}
          />
        </Column>
      </Row>

      <div>
        <YourTasksPanel
          show={isYourTasksPanelOpen}
          onClose={() => setIsYourTasksPanelOpen(false)}
          setSelectedPanelTask={setSelectedPanelTask}
          setShowWorkflowTaskPanel={setShowWorkflowTaskPanel}
          refreshTasks={refreshTasks}
          variant="default"
        />
        <UpcomingMilestonesTaskPanel
          show={isUpcomingMilestonesPanelOpen}
          isDashboard
          onClose={() => setIsUpcomingMilestonesPanelOpen(false)}
          setSelectedPanelTask={setSelectedPanelTask}
          setShowWorkflowTaskPanel={setShowWorkflowTaskPanel}
          refreshTasks={refreshTasks}
        />
        <WorkflowTaskPanel
          task={selectedPanelTask}
          currentStepUuid={selectedPanelTask?.step?.uuid}
          workflow={workflow}
          show={showWorkflowTaskPanel}
          refresh={refreshTasks}
          handleRefresh={handleRefresh}
          onClose={handleCloseWorkflowTaskPanel}
        />
      </div>
    </div>
  );

  return DeveloperSingleCardWithTabsTemplate({
    activeElement: "Dashboard",
    pageTitle: "Dashboard",
    tabsList,
    carousel,
    currentUserType,
    tabsClassName: "DashboardTabs",
  });
};

export default Dashboard;
